import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Headset, Send, UserPlus, Mail, Phone, Loader2, Archive, MessageSquare } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const APP_STATUSES = ["new", "reviewed", "contacted", "hired", "rejected"];

export default function SupportInbox() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "applications" ? "applications" : "support";

  return (
    <div className="space-y-6" data-testid="support-inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Support Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">Live customer conversations and incoming applications.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="support-tab-chats" onClick={() => setParams({})}
            className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors duration-200 ${tab === "support" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] border-[var(--cc-accent)]" : "text-muted-foreground hover:text-foreground"}`}>
            <MessageSquare className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Support
          </button>
          <button data-testid="support-tab-applications" onClick={() => setParams({ tab: "applications" })}
            className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors duration-200 ${tab === "applications" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] border-[var(--cc-accent)]" : "text-muted-foreground hover:text-foreground"}`}>
            <UserPlus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Applications
          </button>
        </div>
      </div>
      {tab === "support" ? <ChatsPanel /> : <ApplicationsPanel />}
    </div>
  );
}

function ChatsPanel() {
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [convo, setConvo] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/support/chats");
      setChats(data);
    } catch {}
  }, []);

  const loadConvo = useCallback(async (sid) => {
    try {
      const { data } = await api.get(`/support/chats/${sid}/full`);
      setConvo(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadChats();
    const t = setInterval(loadChats, 10000);
    return () => clearInterval(t);
  }, [loadChats]);

  useEffect(() => {
    if (!selected) return;
    loadConvo(selected);
    const t = setInterval(() => loadConvo(selected), 5000);
    return () => clearInterval(t);
  }, [selected, loadConvo]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo?.messages?.length]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !selected) return;
    setSending(true);
    try {
      await api.post(`/support/chats/${selected}/reply`, { content: text });
      setReply("");
      await loadConvo(selected);
      loadChats();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSending(false);
  };

  const closeChat = async () => {
    try {
      await api.post(`/support/chats/${selected}/close`);
      toast.success("Conversation closed");
      await loadConvo(selected);
      loadChats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-[340px,1fr] gap-5 items-start">
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="support-chat-list">
        <div className="px-4 py-3 border-b">
          <p className="overline-cc">Conversations — {chats.length}</p>
        </div>
        <div className="max-h-[65vh] overflow-y-auto">
          {chats.length === 0 && (
            <div className="py-14 text-center px-6">
              <Headset className="w-8 h-8 text-muted-foreground mx-auto opacity-40" />
              <p className="text-sm text-muted-foreground mt-3">No support conversations yet. When a customer taps "Talk to a human" in the chat widget, it lands here.</p>
            </div>
          )}
          {chats.map((c) => (
            <button key={c.id} data-testid={`support-chat-item-${c.id}`} onClick={() => setSelected(c.id)}
              className={`w-full text-left px-4 py-3.5 border-b last:border-0 transition-colors duration-150 ${selected === c.id ? "bg-[var(--cc-accent)]/8 border-l-2 border-l-[var(--cc-accent)]" : "hover:bg-secondary/60"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold truncate">{c.name}</p>
                <span className="text-[10px] text-muted-foreground font-mono-cc shrink-0">{timeAgo(c.last_message_at)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] font-mono-cc uppercase px-2 py-0.5 rounded-full border ${c.status === "open" ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" : "text-slate-400 border-slate-500/30 bg-slate-500/10"}`}>{c.status}</span>
                {c.email && <span className="text-[11px] text-muted-foreground truncate">{c.email}</span>}
                {c.unread_admin > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`support-chat-unread-${c.id}`}>{c.unread_admin}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-card flex flex-col h-[70vh]" data-testid="support-conversation-panel">
        {!convo ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <MessageSquare className="w-10 h-10 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground mt-4">Select a conversation to take over.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-5 py-3.5 border-b shrink-0">
              <div>
                <p className="font-heading font-semibold text-sm">{convo.chat.name}</p>
                <p className="text-xs text-muted-foreground">{convo.chat.email || "No email provided"}</p>
              </div>
              {convo.chat.status === "open" && (
                <Button data-testid="support-close-chat" variant="outline" size="sm" className="rounded-full" onClick={closeChat}>
                  <Archive className="w-3.5 h-3.5 mr-1.5" /> Close
                </Button>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
              {convo.messages.map((m) => (
                m.from === "system" ? (
                  <p key={m.id} className="text-[11px] text-muted-foreground text-center italic py-1">{m.content}</p>
                ) : (
                  <div key={m.id} className={`flex ${m.from === "agent" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.from === "agent" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] font-medium"
                        : m.from === "ai" ? "bg-secondary/50 border border-dashed text-muted-foreground"
                        : "bg-secondary border text-foreground"}`}>
                      {m.from === "ai" && <p className="text-[10px] font-mono-cc uppercase tracking-wider mb-1">AI Assistant</p>}
                      {m.from === "agent" && m.agent_name && <p className="text-[10px] font-mono-cc uppercase tracking-wider mb-1 opacity-70">{m.agent_name}</p>}
                      {m.content}
                      <p className={`text-[10px] font-mono-cc mt-1 ${m.from === "agent" ? "opacity-60" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</p>
                    </div>
                  </div>
                )
              ))}
              <div ref={bottomRef} />
            </div>
            {convo.chat.status === "open" && (
              <div className="border-t p-3 flex gap-2 shrink-0">
                <Input data-testid="support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply as CastleCorp team — lands in their chat widget..." />
                <Button data-testid="support-reply-send" onClick={sendReply} disabled={sending || !reply.trim()} size="icon" className="btn-glow shrink-0" aria-label="Send reply">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ApplicationsPanel() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/applications");
      setApps(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/support/applications/${id}`, { status });
      setApps((a) => a.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />;

  return (
    <div className="space-y-4" data-testid="applications-panel">
      {apps.length === 0 && (
        <div className="rounded-xl border bg-card py-14 text-center px-6">
          <UserPlus className="w-8 h-8 text-muted-foreground mx-auto opacity-40" />
          <p className="text-sm text-muted-foreground mt-3">No applications yet. The public Careers page feeds directly into this inbox.</p>
        </div>
      )}
      {apps.map((a) => (
        <div key={a.id} data-testid={`application-card-${a.id}`} className="rounded-xl border bg-card p-6 flex flex-col lg:flex-row lg:items-start gap-5 justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-heading font-semibold">{a.name}</p>
              <span className="overline-cc">{a.role}</span>
              <span className="text-[10px] text-muted-foreground font-mono-cc">{timeAgo(a.created_at)}</span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {a.email}</span>
              {a.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {a.phone}</span>}
            </div>
            {a.experience && <p className="text-sm mt-3"><span className="text-muted-foreground">Experience:</span> {a.experience}</p>}
            {a.message && <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{a.message}</p>}
          </div>
          <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
            <SelectTrigger data-testid={`application-status-${a.id}`} className="w-36 shrink-0 rounded-full h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
